/**
 * CanvasSelectable behavior wrapper for canvas editor mode
 *
 * Extends Selectable with canvas-specific features:
 * - Tool-aware clicks (erase → delete, fill → change color)
 * - Label click detection
 * - Hover tracking
 */

import React, { cloneElement, useState, useCallback, type MouseEvent } from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import type { BlockBehaviorProps } from '../../Types'

interface CanvasSelectableProps extends BlockBehaviorProps {
	blockId: string
	children: React.ReactElement
}

/**
 * Makes a block selectable in canvas editor mode with tool-aware behavior
 */
export function CanvasSelectable({ blockId, children }: CanvasSelectableProps) {
	const context = useBlockRenderContext()
	const [isHovered, setIsHovered] = useState(false)

	// Destructure only the specific properties needed for dependency tracking
	const { selectedIds, currentTool: rawCurrentTool, onDelete, onFill, onLabelClick, onSelect, onHoverChange, mode } = context

	const isSelected = selectedIds?.has(blockId) ?? false
	const currentTool = rawCurrentTool || 'select'

	// Custom click handler for tool-specific behavior
	// All hooks must be called before any conditional returns
	const handleToolClick = useCallback(
		(e: MouseEvent) => {
			e.preventDefault()
			e.stopPropagation()

			// Tool-specific click handling
			switch (currentTool) {
				case 'erase':
					// Erase tool deletes the block
					if (onDelete) {
						onDelete(blockId)
					}
					break

				case 'fill':
					// Fill tool changes block color
					if (onFill) {
						onFill(blockId)
					}
					break

				case 'select':
				default:
					// Single-click opens label dialog for editing
					if (onLabelClick) {
						onLabelClick(blockId, e.clientX, e.clientY)
					} else if (onSelect) {
						// Fallback to selection if no label click handler
						const isAdditive = e.shiftKey
						onSelect(blockId, isAdditive)
					}
					break
			}
		},
		[currentTool, blockId, onDelete, onFill, onLabelClick, onSelect]
	)

	const handleMouseEnter = useCallback(() => {
		setIsHovered(true)
		if (onHoverChange) {
			onHoverChange(blockId, true)
		}
	}, [blockId, onHoverChange])

	const handleMouseLeave = useCallback(() => {
		setIsHovered(false)
		if (onHoverChange) {
			onHoverChange(blockId, false)
		}
	}, [blockId, onHoverChange])

	// Hide native cursor for custom cursor tools; CSS !important is the primary
	// mechanism, but inline 'none' provides a belt-and-suspenders fallback.
	const isCustomCursorTool = currentTool === 'erase' || currentTool === 'fill'
		|| currentTool === 'draw' || currentTool === 'addPlayer'
	const getCursor = useCallback(() => {
		return isCustomCursorTool ? 'none' : 'pointer'
	}, [isCustomCursorTool])

	// Only enable selection in editor mode - after all hooks
	if (mode !== 'editor') {
		return children
	}

	// Clone the child element and add selection styles and event handlers
	const existingStyle = (children.props as any)?.style as React.CSSProperties | undefined

	return cloneElement(children, {
		onClick: handleToolClick,
		onMouseEnter: handleMouseEnter,
		onMouseLeave: handleMouseLeave,
		style: {
			...existingStyle,
			// Add selection ring using CSS variable
			filter: isSelected
				? 'drop-shadow(0 0 3px var(--ring))'
				: isHovered
					? 'drop-shadow(0 0 2px var(--muted-foreground))'
					: existingStyle?.filter,
			cursor: getCursor(),
			// Ensure the element is clickable
			pointerEvents: 'auto',
		},
	} as any)
}
